import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import API from '../api';
/*import PlayerCard from '../components/PlayerCard';*/
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const statsOptions = {
  NBA: ['PTS', 'REB', 'AST', 'STL', 'BLK'],
  NFL: ['PASS_YDS', 'RUSH_YDS', 'REC_YDS', 'PASS_TD', 'RUSH_TD', 'REC_TD', 'COMP']
};

export default function Dashboard() {
  const [bets, setBets] = useState([]);
  const [player, setPlayer] = useState('');
  const [sport, setSport] = useState('NBA');
  const [stat, setStat] = useState(statsOptions['NBA'][0]);
  const [threshold, setThreshold] = useState('');
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [topBets, setTopBets] = useState([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showAllBets, setShowAllBets] = useState(false);
  const navigate = useNavigate();
  
  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('You must be logged in to access the dashboard.');
      navigate('/login');
      return;
    }
    
    // Test token validity by making a request
    API.get('/get_saved_bets')
      .then(res => {
        console.log('Authentication successful, loaded bets:', res.data);
        setBets(res.data.reverse());
      })
      .catch(err => {
        console.error('Authentication failed:', err);
        if (err.response?.status === 401) {
          alert('Your session has expired. Please log in again.');
          localStorage.removeItem('token');
          navigate('/login');
        }
      });

    API.get('/most_confident_bets')
      .then(res => setTopBets(res.data))
      .catch(() => console.log("No top bets"));
  }, [navigate]);
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    setStat(statsOptions[sport][0]);
  }, [sport]);

  const logout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };
    const savePrediction = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You must be logged in to save predictions. Please log in again.');
        navigate('/login');
        return;
      }
      if (!prediction) {
        alert('No prediction to save. Please make a prediction first.');
        return;
      }
      const saveData = {
        player,
        stat_type: stat,
        prediction: prediction.outcome,
        confidence: prediction.confidence,
      };
      const response = await API.post('/save_bet', saveData, {
        headers: { Authorization: `Bearer ${token}` }
        });
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
        // Refresh saved bets
        const res = await API.get('/get_saved_bets', {
        headers: { Authorization: `Bearer ${token}` }
        });
      setBets(res.data.reverse());
    } catch (err) {
      console.error('Save Prediction Error Details:', {
        error: err,
        response: err?.response,
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
        stack: err?.stack
      });
      alert('Failed to save bet: ' + (err?.response?.data?.detail || err?.message || 'Unknown error'));
    }
    };
  const handlePredict = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await API.post('/predict', {
        player,
        sport,
        stat,
        threshold: Number(threshold)
      });
      setPrediction({
        ...res.data,
        predictedThreshold: threshold,
        predictedStat: stat
      });
    } catch {
      alert('Prediction failed, try again.');
    } finally {
      setLoading(false);
    }
  };
  const handleMarkOutcome = async (betId, hit) => {
  try {
    await API.post('/mark_bet_outcome', {
      bet_id: betId,
      hit: hit, // Use the actual hit parameter
    }, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    // Update local bets state to mark this bet as hit/miss
    setBets(prev =>
      prev.map(bet => bet.id === betId ? { ...bet, actual_result: hit } : bet)
    );
  } catch {
    alert('Failed to mark outcome');
  }
};

  return (
    <div style={{ 
      background: '#111111',
      minHeight: '100vh', 
      width: '100vw', 
      padding: '2rem 4vw', 
      color: '#e0e0e0', 
      fontFamily: 'Poppins, sans-serif', 
      overflowX: 'hidden', 
      boxSizing: 'border-box', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      position: 'relative'
    }}>
      {/* Animated Background Particles */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1
      }}>
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              width: '2px',
              height: '2px',
              background: '#7CFC00',
              borderRadius: '50%',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Glowing Orb Effect */}
      <motion.div
        style={{
          position: 'fixed',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(124, 252, 0, 0.08) 0%, rgba(124, 252, 0, 0.03) 50%, transparent 100%)',
          borderRadius: '50%',
          left: mousePosition.x - 150,
          top: mousePosition.y - 150,
          pointerEvents: 'none',
          zIndex: 2,
          filter: 'blur(30px)',
        }}
        animate={{
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Shared container for header only */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '.1rem', marginBottom: '1.4rem' }}>
        <motion.header 
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{ 
            width: '100%',
            maxWidth: '1000px',
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: 'rgba(10, 10, 10, 0.8)',
            backdropFilter: 'blur(20px)',
            padding: '.2rem 2rem',
            borderRadius: '20px',
            border: '1px solid rgba(124, 252, 0, 0.2)',
            boxShadow: '0 0 30px rgba(124, 252, 0, 0.1)',
          }}
        >
          <motion.img 
            src="/logo.png" 
            alt="StatPulseAI Logo" 
            style={{ height: '70px' }}
            whileHover={{ scale: 1.05 }}
            transition={{ type: 'spring', stiffness: 300 }}
          />
          <motion.button 
            onClick={logout} 
            whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(124, 252, 0, 0.4)' }}
            whileTap={{ scale: 0.95 }}
            style={{ 
              background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 100%)',
              border: 'none', 
              borderRadius: '12px', 
              padding: '0.8rem 1.5rem', 
              fontWeight: 'bold', 
              fontSize: '1rem', 
              cursor: 'pointer', 
              color: '#000', 
              transition: 'all 0.3s ease',
              boxShadow: '0 0 15px rgba(124, 252, 0, 0.3)',
            }}
          >
          Log Out
          </motion.button>
        </motion.header>
      </div>

      {/* Shared container for AI Prediction Engine */}
      <div style={{ width: '100%', maxWidth: '750px', margin: '0 auto' }}>
        {/* AI Prediction Engine Section */}
        <motion.form 
          onSubmit={handlePredict} 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{ 
            width: '100%',
            margin: '0 auto 3rem auto',
            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(20, 30, 40, 0.9) 100%)',
            borderRadius: '24px', 
            color: '#e0e0e0', 
            boxShadow: '0 0 40px rgba(124, 252, 0, 0.2), inset 0 0 40px rgba(124, 252, 0, 0.05)', 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '1.5rem', 
            fontFamily: 'Poppins, sans-serif', 
            boxSizing: 'border-box',
            border: '1px solid rgba(124, 252, 0, 0.1)',
            backdropFilter: 'blur(20px)',
            position: 'relative',
            overflow: 'hidden',
            padding: '0.9rem',
          }}
        >
          {/* Background Grid Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(rgba(124, 252, 0, 0.02) 1px, transparent 1px),
              linear-gradient(90deg, rgba(124, 252, 0, 0.02) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px',
            opacity: 0.5,
          }} />

          <motion.h2 
            style={{ 
              fontSize: '2.5rem', 
              color: '#7CFC00', 
              textAlign: 'center',
              marginBottom: '1rem',
              background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 20px rgba(124, 252, 0, 0.3)',
              fontWeight: '800',
            }}
            whileHover={{ scale: 1.02 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            Prediction Model
          </motion.h2>

          <motion.input 
            type="text" 
            placeholder="Player name" 
            value={player} 
            onChange={e => setPlayer(e.target.value)} 
            style={{ 
              padding: '1rem 1.2rem', 
              fontSize: '1.1rem', 
              borderRadius: '12px', 
              border: '1px solid rgba(124, 252, 0, 0.2)', 
              background: 'rgba(20, 30, 40, 0.8)', 
              color: '#e0e0e0', 
              outline: 'none', 
              fontFamily: 'Poppins, sans-serif', 
              width: '100%', 
              boxSizing: 'border-box',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
            }}
            whileFocus={{ scale: 1.02, boxShadow: '0 0 20px rgba(124, 252, 0, 0.3)' }}
            required 
          />
          
          <motion.select 
            value={sport} 
            onChange={e => setSport(e.target.value)} 
            style={{ 
              padding: '1rem 1.2rem', 
              fontSize: '1.1rem', 
              borderRadius: '12px', 
              border: '1px solid rgba(124, 252, 0, 0.2)', 
              background: 'rgba(20, 30, 40, 0.8)', 
              color: '#e0e0e0', 
              fontFamily: 'Poppins, sans-serif', 
              width: '100%', 
              boxSizing: 'border-box',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
            }}
            whileFocus={{ scale: 1.02, boxShadow: '0 0 20px rgba(124, 252, 0, 0.3)' }}
          >
          {Object.keys(statsOptions).map(s => <option key={s} value={s}>{s}</option>)}
          </motion.select>
          
          <motion.select 
            value={stat} 
            onChange={e => setStat(e.target.value)} 
            style={{ 
              padding: '1rem 1.2rem', 
              fontSize: '1.1rem', 
              borderRadius: '12px', 
              border: '1px solid rgba(124, 252, 0, 0.2)', 
              background: 'rgba(20, 30, 40, 0.8)', 
              color: '#e0e0e0', 
              fontFamily: 'Poppins, sans-serif', 
              width: '100%', 
              boxSizing: 'border-box',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
            }}
            whileFocus={{ scale: 1.02, boxShadow: '0 0 20px rgba(124, 252, 0, 0.3)' }}
          >
          {statsOptions[sport].map(st => <option key={st} value={st}>{st}</option>)}
          </motion.select>
          
          <motion.input 
            type="number" 
            placeholder="Threshold amount" 
            value={threshold} 
            onChange={e => setThreshold(e.target.value)} 
            min="0" 
            step="any" 
            style={{ 
              padding: '1rem 1.2rem', 
              fontSize: '1.1rem', 
              borderRadius: '12px', 
              border: '1px solid rgba(124, 252, 0, 0.2)', 
              background: 'rgba(20, 30, 40, 0.8)', 
              color: '#e0e0e0', 
              outline: 'none', 
              fontFamily: 'Poppins, sans-serif', 
              width: '100%', 
              boxSizing: 'border-box',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
            }}
            whileFocus={{ scale: 1.02, boxShadow: '0 0 20px rgba(124, 252, 0, 0.3)' }}
            required 
          />
          
          <motion.button 
            type="submit" 
            disabled={loading} 
            whileHover={{ scale: 1.02, boxShadow: '0 0 30px rgba(124, 252, 0, 0.5)' }}
            whileTap={{ scale: 0.98 }}
            style={{ 
              padding: '1.2rem', 
              fontSize: '1.3rem', 
              borderRadius: '15px', 
              border: 'none', 
              background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 100%)', 
              color: '#000', 
              cursor: loading ? 'wait' : 'pointer', 
              fontWeight: '700', 
              boxShadow: '0 0 25px rgba(124, 252, 0, 0.4)', 
              fontFamily: 'Poppins, sans-serif', 
              transition: 'all 0.3s ease', 
              width: '100%', 
              boxSizing: 'border-box',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <span style={{ position: 'relative', zIndex: 2 }}>
          {loading ? 'Predicting...' : 'Predict'}
            </span>
            {!loading && (
              <motion.div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                }}
                whileHover={{ left: '100%' }}
                transition={{ duration: 0.6 }}
              />
            )}
          </motion.button>

        {prediction && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              style={{ 
                marginTop: '2rem', 
                color: prediction.outcome === 'OVER' ? '#7CFC00' : '#ff6347', 
                fontWeight: '700', 
                fontSize: '1.4rem', 
                fontFamily: 'Poppins, sans-serif', 
                textAlign: 'center',
                background: 'rgba(20, 30, 40, 0.6)',
                padding: '2rem',
                borderRadius: '20px',
                border: '1px solid rgba(124, 252, 0, 0.2)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <motion.h3 
                style={{ marginBottom: '1rem' }}
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                {prediction.outcome} {prediction.predictedThreshold} {prediction.predictedStat} with {prediction.confidence ?? 0}%
              </motion.h3>
              {prediction.warning && (
                <motion.p 
                  style={{ color: '#ffcc00', fontWeight: '500', fontSize: '1rem' }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  ⚠️ {prediction.warning}
                </motion.p>
              )}              
              <div style={{ 
                background: 'rgba(10, 20, 30, 0.8)', 
                borderRadius: '15px', 
                overflow: 'hidden', 
                height: '30px', 
                width: '100%', 
                boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.5)',
                marginTop: '1rem',
                border: '1px solid rgba(124, 252, 0, 0.2)',
              }}>
                <motion.div 
                  style={{ 
                    height: '100%', 
                    width: `${prediction.confidence}%`, 
                    background: prediction.outcome === 'OVER' ? 'linear-gradient(90deg, #7CFC00, #00ff88)' : 'linear-gradient(90deg, #ff6347, #ff4757)',
                    borderRadius: '15px',
                  }}
                  initial={{ width: 0 }}
                  animate={{ width: `${prediction.confidence}%` }}
                  transition={{ duration: 1.5, ease: 'easeOut' }}
                />
              </div>
            </motion.div>
          )}

          {prediction && (
            <>
              <motion.button
                type="button"
            onClick={savePrediction}
                whileHover={{ scale: 1.05, boxShadow: '0 0 25px rgba(0, 28, 61, 0.6)' }}
                whileTap={{ scale: 0.95 }}
            style={{
                  marginTop: '1.5rem',
                  background: 'linear-gradient(135deg, #001c3d 0%, #004080 100%)',
            color: '#7CFC00',
                  padding: '1rem 2rem',
            fontWeight: 'bold',
                  borderRadius: '12px',
                  border: '2px solid #001c3d',
                  fontSize: '1.1rem',
            cursor: 'pointer',
            alignSelf: 'center',
                  width: 'fit-content',
                  boxShadow: '0 0 20px rgba(0, 28, 61, 0.4)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  zIndex: 1000,
            }}
        >
            Save Prediction
              </motion.button>
              {saveSuccess && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  style={{
                    marginTop: '1rem',
                    color: '#7CFC00',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    textAlign: 'center',
                    background: 'rgba(124, 252, 0, 0.08)',
                    borderRadius: '8px',
                    padding: '0.7rem 1.5rem',
                    border: '1px solid #7CFC00',
                    boxShadow: '0 0 10px rgba(124,252,0,0.15)',
                    alignSelf: 'center',
                  }}
                  transition={{ duration: 0.5 }}
                >
                  Prediction saved!
                </motion.div>
              )}
            </>
          )}

          {prediction && prediction.recent_stats && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              style={{ 
                width: '100%', 
                margin: '2rem auto',
                background: 'rgba(20, 30, 40, 0.6)',
                padding: '2rem',
                borderRadius: '20px',
                border: '1px solid rgba(124, 252, 0, 0.2)',
                backdropFilter: 'blur(10px)',
                overflowX: 'auto',
                boxSizing: 'border-box',
              }}
            >
              <h4 style={{ textAlign: 'center', color: '#7CFC00', marginBottom: '1.5rem', fontSize: '1.3rem' }}>Last 5 Game Stats</h4>
              <div style={{ width: '100%', minWidth: 0 }}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={prediction.recent_stats.map((val, i) => ({ game: `G${i + 1}`, value: val }))}>
                    <XAxis dataKey="game" stroke="#ccc" />
                    <YAxis stroke="#ccc" />
                    <Tooltip />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {prediction.recent_stats.map((val, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={val >= parseFloat(threshold) ? '#7CFC00' : '#FF6347'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          )}
        </motion.form>
              </div>

      {/* Saved Bets Section */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '3rem' }}>
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          style={{ 
            width: '100%',
            maxWidth: '700px',
            background: 'rgba(20, 30, 40, 0.6)',
            padding: '0.7rem',
            borderRadius: '20px',
            border: '1px solid rgba(124, 252, 0, 0.2)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <motion.h4 
            style={{ 
              color: '#7CFC00', 
              marginBottom: '2rem', 
              textAlign: 'center',
              fontSize: '1.8rem',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
            whileHover={{ scale: 1.02 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            Saved Bets
          </motion.h4>
      {bets.length === 0 ? (
            <motion.p 
              style={{ 
                color: '#ccc', 
                fontFamily: 'Poppins, sans-serif', 
                textAlign: 'center',
                fontSize: '1.1rem',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              No saved bets yet.
            </motion.p>
      ) : (
        <>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1.5rem',
              width: '100%'
            }}>
              {(showAllBets ? bets : bets.slice(0, 4)).map((bet, index) => (
                <motion.div 
                  key={bet.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(124, 252, 0, 0.2)' }}
                  style={{ 
                    background: 'rgba(10, 20, 30, 0.8)', 
                    padding: '1.5rem', 
                    borderRadius: '15px', 
                    color: '#e0e0e0',
                    border: '1px solid rgba(124, 252, 0, 0.1)',
                    backdropFilter: 'blur(10px)',
                    minWidth: '280px',
                    maxWidth: '400px',
                  }}
                >
                  <p><strong style={{ color: '#7CFC00' }}>Player:</strong> {bet.player}</p>
                  <p><strong style={{ color: '#7CFC00' }}>Stat:</strong> {bet.stat_type}</p>
                  <p><strong style={{ color: '#7CFC00' }}>Prediction:</strong> {bet.prediction}</p>
                  <p><strong style={{ color: '#7CFC00' }}>Confidence:</strong> {bet.confidence.toFixed(2)}%</p>

        {bet.actual_result === null ? (
                    <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                      <motion.button
              onClick={() => handleMarkOutcome(bet.id, true)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
              style={{
                          background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 100%)',
                border: 'none',
                          borderRadius: '8px',
                          padding: '0.6rem 1rem',
                cursor: 'pointer',
                fontWeight: 'bold',
                color: '#000',
                          boxShadow: '0 0 15px rgba(124, 252, 0, 0.3)',
              }}
            >
              ✅ Hit
                      </motion.button>
                      <motion.button
              onClick={() => handleMarkOutcome(bet.id, false)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
              style={{
                          background: 'linear-gradient(135deg, #ff6347 0%, #ff4757 100%)',
                border: 'none',
                          borderRadius: '8px',
                          padding: '0.6rem 1rem',
                cursor: 'pointer',
                fontWeight: 'bold',
                color: '#fff',
                          boxShadow: '0 0 15px rgba(255, 99, 71, 0.3)',
              }}
            >
              ❌ Miss
                      </motion.button>
          </div>
        ) : (
                    <motion.p 
                      style={{ 
                        marginTop: '1rem', 
                        fontWeight: '700', 
                        color: bet.actual_result ? '#7CFC00' : '#ff6347',
                        fontSize: '1.1rem',
                      }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
            {bet.actual_result ? '✅ Hit' : '❌ Miss'}
                    </motion.p>
        )}
                </motion.div>
              ))}
          </div>
          {bets.length > 4 && (
            <motion.button
              onClick={() => setShowAllBets(v => !v)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                margin: '2rem auto 0 auto',
                display: 'block',
                background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 100%)',
                color: '#000',
                fontWeight: 'bold',
                padding: '0.8rem 2rem',
                borderRadius: '10px',
                border: 'none',
                fontSize: '1.1rem',
                cursor: 'pointer',
                boxShadow: '0 0 15px rgba(124, 252, 0, 0.2)',
                transition: 'all 0.3s ease',
              }}
            >
              {showAllBets ? 'Show Less' : 'See All'}
            </motion.button>
          )}
        </>
      )}
        </motion.div>
      </div>

      {topBets.length > 0 && (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '3rem' }}>
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            style={{ 
              width: '100%',
              maxWidth: '700px',
              textAlign: 'center', 
              background: 'rgba(20, 30, 40, 0.6)',
              padding: '0.7rem',
              borderRadius: '20px',
              border: '1px solid rgba(124, 252, 0, 0.2)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <motion.h3 
              style={{ 
                color: '#7CFC00', 
                marginBottom: '2rem',
                fontSize: '2rem',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #7CFC00 0%, #00ff88 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
              whileHover={{ scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              🔥 Top 3 Confident Picks
            </motion.h3>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            {topBets.map((bet, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + i * 0.1, duration: 0.5 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(10, 20, 30, 0.8) 0%, rgba(20, 30, 40, 0.9) 100%)', 
                    padding: '1.5rem', 
                    borderRadius: '15px', 
                    boxShadow: '0 0 25px rgba(124,252,0,0.2)', 
                    width: '280px',
                    border: '1px solid rgba(124, 252, 0, 0.2)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <p><strong style={{ color: '#7CFC00' }}>Player:</strong> {bet.player}</p>
                  <p><strong style={{ color: '#7CFC00' }}>Stat:</strong> {bet.stat_type}</p>
                  <p><strong style={{ color: '#7CFC00' }}>Prediction:</strong> {bet.prediction}</p>
                  <p><strong style={{ color: '#7CFC00' }}>Confidence:</strong> {bet.confidence.toFixed(2)}%</p>
                </motion.div>
            ))}
          </div>
          </motion.div>
        </div>
      )}

    </div>
  );
}
